import { Autocomplete, Divider, Grid, ListItemIcon, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import contactStyles from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import popupStyles from "../../../assets/styles/componentStyles/globalPopupStyles.module.scss"
import Required from '../../utility/required';
import GlobalButton from '../../utility/globalButton/button';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { DataConnectorContext, DataConnectorContextpayload } from '../../../context/dataConnectorContext';
import style from "../../../assets/styles/componentStyles/customApiStyles/customApiStyle.module.scss";
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import globalStyles from "../../../assets/styles/global.module.scss";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import deleteDoc from "../../../assets/deleteDoc.svg";
import { createDataConnectorAssignementPayload, DataConnectorAssignment, dataConnectorAssignmentType, dataConnectorList, voiceAgentListInterface } from '../../../models/axiosModel';
import GlobalDeletePopup from '../../utility/globalDeletePopup/globalDeletePopup';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import { useVoiceAgentStore } from '../../../store/voiceAgentStore';

export const isVoiceAgentDataConnectorAssignment = (assignment: DataConnectorAssignment) => {
    return assignment?.data_connector_assignment_type_name?.toLowerCase() === 'voice agent';
}

export const getVoiceAgentAssignmentUniqueId = (assignment: DataConnectorAssignment) => {
    return (assignment.voice_agent_id || assignment.data_connector_assignment_unique_id)?.toString() || "";
}

interface AssignVoiceAgentPopupProps {
    selectedDataConnector: dataConnectorList,
    setShowAssignedVoiceAgentPopup: React.Dispatch<React.SetStateAction<boolean>>,
}

const AssignVoiceAgentPopup: React.FC<AssignVoiceAgentPopupProps> = (props) => {

    const { selectedDataConnector, setShowAssignedVoiceAgentPopup } = props;

    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { dataConnectorAssignmentType, getDataConnectorAssignmentType, assignDataConnectorAssignment, getChatbotDataConnectorList, removeDataConnectorAssignment } = useContext(DataConnectorContext) as DataConnectorContextpayload;

    const voiceAgentList = useVoiceAgentStore((state) => state.voiceAgentList);
    const getVoiceAgentListAPI = useVoiceAgentStore((state) => state.getVoiceAgentListAPI);

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const getVoiceAssignmentTypeId = () => {
        return dataConnectorAssignmentType.find(
            (assignment: dataConnectorAssignmentType) => assignment.data_connector_assignment_type_name.toLowerCase() === "voice agent"
        )?.data_connector_assignment_type_id ?? 0;
    }

    const [payload, setPayload] = useState<createDataConnectorAssignementPayload>({
        chatbot_data_connector_id: selectedDataConnector?.chatbot_data_connector_id,
        data_connector_assignment_type_id: getVoiceAssignmentTypeId(),
        data_connector_assignment_unique_id: "",
    });

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<string | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [selectedVoiceAgent, setSelectedVoiceAgent] = useState<voiceAgentListInterface | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: string) => {
        event.stopPropagation();
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    const menuItem = [
        {
            label: "Remove",
            icon: <img alt="" src={deleteDoc} />,
            action: (voiceAgent: voiceAgentListInterface) => {
                setSelectedVoiceAgent(voiceAgent);
                setShowDeleteConfirmation(true);
            }
        }
    ]

    const getAssignedVoiceAgentAssignments = () => {
        return selectedDataConnector?.data_connector_assignment?.filter(isVoiceAgentDataConnectorAssignment) || [];
    }

    const getAssignedVoiceAgentsForDisplay = (): Array<{ voice_agent_id: string, voice_agent_name: string, assignment: DataConnectorAssignment }> => {
        return getAssignedVoiceAgentAssignments().map((assignment) => {
            const voiceAgentId = getVoiceAgentAssignmentUniqueId(assignment);
            const fromList = voiceAgentList?.find((agent) => agent.voice_agent_id === voiceAgentId);
            return {
                voice_agent_id: voiceAgentId,
                voice_agent_name: assignment.voice_agent_name || fromList?.voice_agent_name || voiceAgentId,
                assignment,
            };
        }).filter((item) => item.voice_agent_id);
    }

    const getAvailableVoiceAgentOptions = () => {
        const assignedIds = new Set(getAssignedVoiceAgentAssignments().map(getVoiceAgentAssignmentUniqueId));
        return (voiceAgentList || []).filter((agent) => !assignedIds.has(agent.voice_agent_id));
    }

    const getDataConnectorAssignmentTypeApiCall = async () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        const res = await getDataConnectorAssignmentType();
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    const getVoiceAgentListApiCall = async () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        const res = await getVoiceAgentListAPI();
        if (!(res.status)) {
            handleError(res);
        }
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    useEffect(() => {
        if (dataConnectorAssignmentType.length === 0) {
            getDataConnectorAssignmentTypeApiCall();
        }
        if (!voiceAgentList) {
            getVoiceAgentListApiCall();
        }
    }, []);

    useEffect(() => {
        setPayload((prevState) => ({
            ...prevState,
            data_connector_assignment_type_id: getVoiceAssignmentTypeId(),
        }));
    }, [dataConnectorAssignmentType]);

    const handleVoiceAgentChange = (value: voiceAgentListInterface | null) => {
        setPayload((prevState) => ({
            ...prevState,
            data_connector_assignment_unique_id: value ? value.voice_agent_id : "",
            data_connector_assignment_type_id: getVoiceAssignmentTypeId(),
        }));
    }

    const checkDisabledAssignBtn = () => {
        return !payload.data_connector_assignment_unique_id || payload.data_connector_assignment_unique_id === "";
    }

    const handleAssignClick = async () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        const res = await assignDataConnectorAssignment({
            ...payload,
            data_connector_assignment_type_id: getVoiceAssignmentTypeId(),
            data_connector_assignment_unique_id: String(payload.data_connector_assignment_unique_id),
        });
        if (res.status) {
            showSuccess("Voice Agent Assigned Successfully")
            getChatbotDataConnectorList();
        } else {
            handleError(res);
        }
        setShowAssignedVoiceAgentPopup(false);
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    const handleRemoveVoiceAgentAssignment = async () => {
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: true }));
        if (selectedVoiceAgent) {
            const data_connector_assignement_id = selectedDataConnector.data_connector_assignment?.find(
                (assignment: DataConnectorAssignment) =>
                    isVoiceAgentDataConnectorAssignment(assignment)
                    && getVoiceAgentAssignmentUniqueId(assignment) === selectedVoiceAgent.voice_agent_id
            )?.data_connector_assignment_id;
            if (data_connector_assignement_id) {
                const res = await removeDataConnectorAssignment(data_connector_assignement_id);
                if (res.status) {
                    showSuccess("Voice Agent Removed Successfully")
                    await getChatbotDataConnectorList();
                } else {
                    handleError(res);
                }
            }
        }
        setShowAssignedVoiceAgentPopup(false);
        setFlagLoading(prevState => ({ ...prevState, flagRoundLoader: false }));
    }

    const assignedVoiceAgents = getAssignedVoiceAgentsForDisplay();

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container direction={"column"} justifyContent={"center"}>
                        <Grid item textAlign={"center"}>
                            <Typography className={popupStyles.popupTitleTypography}>{`Assign Voice Agent to ${selectedDataConnector?.data_connector_functions?.data_connector_function_name}`}</Typography>
                        </Grid>

                        <Divider className={popupStyles.popupTitleDivider} />

                        <Grid item>
                            <Grid container className={popupStyles.popupBodyGridContainer} gap={1}>
                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={popupStyles.popupFieldTypography}>Select Voice Agent <Required /></Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                className={`${contactStyles.contactFilterDropdownsInPopup}`}
                                                options={getAvailableVoiceAgentOptions()}
                                                getOptionLabel={(option: voiceAgentListInterface) => option?.voice_agent_name ?? ''}
                                                value={voiceAgentList?.find((agent) => agent.voice_agent_id === payload.data_connector_assignment_unique_id) || null}
                                                renderInput={(params) => <TextField {...params} placeholder='Select Voice Agent' />}
                                                onChange={(_, value: voiceAgentListInterface | null) => handleVoiceAgentChange(value)}
                                                renderOption={(props, option) => {
                                                    return (
                                                        <li {...props} key={option?.voice_agent_id}>
                                                            {option?.voice_agent_name ?? ''}
                                                        </li>
                                                    );
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} padding={"20px 0"}>
                                <Grid item>
                                    <GlobalButton
                                        buttonText="Cancel"
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='borderedButtonStyle'
                                        onClick={() => setShowAssignedVoiceAgentPopup(false)}
                                    />
                                </Grid>
                                <Grid item>
                                    <GlobalButton
                                        buttonText={"Assign"}
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='primaryButtonStyle'
                                        disabled={checkDisabledAssignBtn()}
                                        onClick={() => handleAssignClick()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Grid container className={popupStyles.popupBodyGridContainer} gap={1}>
                                <Grid item xs={12}>
                                    <Grid container gap={"12px"}>
                                        <Grid item xs={12}>
                                            <Typography className={style.assignedChatbotText}>Assigned Voice Agent</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TableContainer className={`${style.customAPITableContainer} ${style.assignedChatbotTableContainer}`}>
                                                <Table stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell className={dashboardStyles.tableBodyHeadingFontStyle}>Voice Agent Name</TableCell>
                                                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.tableHeaderMorevertCell}`}></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {assignedVoiceAgents.length > 0 ?
                                                            assignedVoiceAgents.map((voiceAgent, index) => {
                                                                return (
                                                                    <TableRow key={index} className={`${dashboardStyles.cellRow}`}>
                                                                        <TableCell className={dashboardStyles.tableBodySubHeadingFontStyle}>{voiceAgent.voice_agent_name || ""}</TableCell>
                                                                        <TableCell className={`${dashboardStyles.tableBodySubHeadingFontStyle} ${style.lastColumnSticky} ${style.moreVertIcon}`} onClick={(e) => e.stopPropagation()}>
                                                                            <MoreVertIcon
                                                                                className={`${globalStyles.clickable}`}
                                                                                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => handleMenuOpen(e, voiceAgent.voice_agent_id)}
                                                                            />
                                                                            <Menu
                                                                                anchorEl={anchorEl}
                                                                                keepMounted
                                                                                open={Boolean(anchorEl) && voiceAgent.voice_agent_id === currentRowIndex}
                                                                                onClose={handleMenuClose}
                                                                            >
                                                                                {menuItem.map((item, index) => {
                                                                                    return (
                                                                                        <MenuItem
                                                                                            className={`${globalStyles.menuItemStyle} ${globalStyles.secondaryFontSize}`}
                                                                                            key={index}
                                                                                            onClick={() => {
                                                                                                handleMenuClose();
                                                                                                item.action({
                                                                                                    voice_agent_id: voiceAgent.voice_agent_id,
                                                                                                    voice_agent_name: voiceAgent.voice_agent_name,
                                                                                                } as voiceAgentListInterface);
                                                                                            }}
                                                                                        >
                                                                                            <ListItemIcon>
                                                                                                {item.icon}
                                                                                            </ListItemIcon>
                                                                                            <span>
                                                                                                {item.label}
                                                                                            </span>
                                                                                        </MenuItem>
                                                                                    );
                                                                                })}
                                                                            </Menu>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })
                                                            :
                                                            <TableRow className={dashboardStyles.cellRow}>
                                                                <TableCell colSpan={12} className={`${globalStyles.textCenter}`}>
                                                                    No Data Available
                                                                </TableCell>
                                                            </TableRow>
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {showDeleteConfirmation &&
                <GlobalDeletePopup
                    deleteDescription={`Are you sure want to remove ${selectedVoiceAgent?.voice_agent_name} voice agent from ${selectedDataConnector?.data_connector_functions?.data_connector_function_name}`}
                    title="Remove Confirmation"
                    primaryButtonText="Remove"
                    open={showDeleteConfirmation}
                    onClose={() => setShowDeleteConfirmation(false)}
                    onDelete={() => {
                        if (selectedVoiceAgent) {
                            handleRemoveVoiceAgentAssignment()
                        }
                        setShowDeleteConfirmation(false)
                    }}
                />
            }
        </>
    )
}

export default AssignVoiceAgentPopup
